import {Component, DestroyRef, OnInit} from '@angular/core';
import {ActivatedRoute, ParamMap, Router} from '@angular/router';
import {Model} from '../../app.model';
import {runInAction} from 'mobx';
import {SubscriptionService} from '../subscription.service';
import {Subscription} from '../subscription.model';
import {AutorunComponent} from '@myshared/autorun.component';
import {observable} from '@myshared/mobx-connector';
import {Block} from '../../phonenumber/phonenumber.model';
import {PhonenumberService} from '../../phonenumber/phonenumber.service';
import { IntegrationAppsService } from 'src/app/integration-apps/integration-apps.service';
import { GetPbxIntegrationAppInstallationsRequest } from '@proto/odoo/integrations';
import { exhaustMap, finalize, firstValueFrom, takeWhile, timer } from 'rxjs';
import { ToastService } from 'src/app/toast.service';
import { I18NextService } from 'angular-i18next';
import { AppWithVersion } from 'src/app/integration-apps/integration-apps.types';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-subscription-view',
    templateUrl: './subscription-view.component.html',
    styleUrls: [],
    standalone: false
})
export class SubscriptionViewComponent extends AutorunComponent implements OnInit {

  public subscription: Subscription;
  public trunks: Block[] = [];
  public hasTrunkAccess = false;
  public isPartner = false;
  public isAdmin: boolean;
  public installedIntegrationApps?: AppWithVersion[] = [];
  public idsOfInstalledApps?: string[];

  public dialogContentType: string = '';
  public showDialog: boolean = false;
  public idOfAppToUninstall: string = '';
  public hasIntegrationAppsBetaTag: boolean;

  private installedIntegrationAppsPolling = false;

  @observable private applianceId$: string;

  constructor(private route: ActivatedRoute,
              private router: Router,
              private m: Model,
              private subscriptionService: SubscriptionService,
              private phonenumberService: PhonenumberService,
              private integrationService: IntegrationAppsService,
              private toastService: ToastService,
              private i18Next: I18NextService,
              private destroyRef: DestroyRef) {
    super();
  }

  ngOnInit() {
    this.route.paramMap.subscribe((params: ParamMap) => {
      if (params.has('applianceId')) {
        runInAction(() => {
          this.applianceId$ = params.get('applianceId');
        });
        this.subscriptionService.getSubscriptionDetails(+this.applianceId$)
          .subscribe({
            next: (r) => {
              this.subscription = Subscription.fromJson(r);
              // When we don't get a subscription, redirect
              if (!this.subscription) { this.redirect(); return; }
              this.loadBootstate();
              this.hasIntegrationAppsBetaTag = this.m.account.isIntegrationAppsBetaUser;
              if (this.hasIntegrationAppsBetaTag) { 
                this.startInstalledIntegrationAppsPolling();
              }
            },
            error: () => {
              // When the subscription not exist anymore, the server will send back a 500,
              // in this case redirect
              this.redirect();
            }
          });
      } else {
        this.router.navigate(['/subscriptions']);
      }
    });

    this.autorun(() => {
      this.isAdmin = this.m.account.isMypascomAdmin;
      this.hasTrunkAccess = this.m.account.hasTrunkAccess
      this.isPartner = this.m.account.currentUser.companyIsPartner;
    });

    this.autorun(() => {
      this.trunks = this.m.phonenumber.blocks.filter(t => {
          if (+t.appliance?.appliance_id === +(this.applianceId$)) {
            t.appliance = this.subscription;
            return true;
          }
          return false;
        }
      );
    });
    if (this.hasTrunkAccess) {
      this.phonenumberService.refresh(true);
    }
  }

  private async startInstalledIntegrationAppsPolling(): Promise<void> {
    if (this.installedIntegrationAppsPolling) {
      return;
    }
    this.installedIntegrationAppsPolling = true;
    const hasPendingInstallation = await this.loadInstalledIntegrationApps();

    if (!hasPendingInstallation) {
      this.installedIntegrationAppsPolling = false;
      return;
    }

    timer(20_000, 20_000)
      .pipe(
        exhaustMap(() => this.loadInstalledIntegrationApps()),
        takeWhile(hasPending => hasPending, true),
        finalize(() => {
          this.installedIntegrationAppsPolling = false;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  public onBack() {
    if (this.subscription.is_my) {
      this.redirectToMySubscriptions();
    } else {
      this.redirect();
    }
  }

  public get hasPhonenumbers() {
    return this.hasTrunkAccess && this.subscription &&
      (this.subscription.isOne || this.subscription.isOneTrial) && this.trunks.length > 0;
  }

  get hasTariffDetail(): boolean {
    return !!this.subscription?.tariffPlanDetail?.currentPlan
      && this.subscription?.tariffPlanDetail?.currentPlan !== 'PC-ONE-TARIFF-TRIAL';
  }

  get showItemizedBilling(): boolean {
    return !!this.subscription?.tariffPlanDetail?.currentPlan
        && this.subscription?.tariffPlanDetail?.currentPlan !== 'PC-ONE-TARIFF-TRIAL';
  }

  public get hasTrunkCards() {
    return (this.hasTrunkAccess || this.isPartner) && this.subscription && this.subscription.isOne && (this.hasTariffDetail || this.showItemizedBilling);
  }

  public get status(): { status: string; color: any, messageBg: string } {
    const pbxStatus = this.subscription.pbxStatus;
    if (pbxStatus) {
      pbxStatus.status += '_tooltip';
    }
    return pbxStatus;
  }

  get hasIntegrationAppsInstallations(): boolean {
    return (this.installedIntegrationApps?.length ?? 0) > 0;
  }

  private redirect() {
    this.router.navigate(['/']);
  }

  private redirectToMySubscriptions() {
    this.router.navigate(['/subscriptions/my']);
  }

  /**
   * Load the bootstate for this given subscriptions if parent name matches
   *
   * If all subscriptions were loaded, we will get the data from subscriptions bootstate,
   * otherwise we will load this data with a single [subscription] array item.
   * 
   * @private
   */
  private loadBootstate() {
    if (this.m.subscription.subscriptionBootstates.length > 0) {
      this.subscription.setBootstate(this.m.subscription.findBootstate(this.subscription));
    } else {
      this.subscriptionService.getSubscriptionsBootstate([this.subscription]).subscribe(r => {
        const bootstate = r.find(f => f.instance_name === this.subscription.instance_name);
        this.subscription.setBootstate(bootstate ? bootstate.bootstate : 'unknown');
      }, error => {
        this.subscription.setBootstate('unknown');
      });
    }
  }

  private async loadInstalledIntegrationApps(): Promise<boolean> {
    try {
      const appInstallationsReq = GetPbxIntegrationAppInstallationsRequest.create({
        applianceId: +this.subscription.appliance_id,
      });

      const installationsResponse = await firstValueFrom(
        this.integrationService.getAppInstallations(appInstallationsReq)
      );
      const installedIntegrationApps: AppWithVersion[] = [];
      let hasPendingInstallation = false;

      for (const inst of installationsResponse.installations ?? []) {
        const installation = inst.installation;
        const state = installation?.state;
        if (this.integrationService.isPendingState(state)) {
          hasPendingInstallation = true;
        }
        const installedApp: AppWithVersion = {
          app: installation?.app,
          installedVersion: installation?.installedVersion,
          state: state,
        }
        installedIntegrationApps.push(installedApp);
      }
      
      this.installedIntegrationApps = installedIntegrationApps;
      return hasPendingInstallation;
    } catch (error) {
      this.toastService.error(this.i18Next.t('subscription-view_load_integration_apps_error'), 'error');
      return false;
    }
  }

  confirmUninstallation(appId: string) {
    this.idOfAppToUninstall = appId;
    this.dialogContentType = 'confirmUninstallation';
    this.showDialog = true;
  }

  hideTheDialog() {
    this.showDialog = false;
  }

  async startUninstallation() {
    const success = await this.integrationService.uninstallAppWithFeedback(
      parseInt(this.subscription.appliance_id),
      this.idOfAppToUninstall
    );
    if (success) {
      this.hideTheDialog();
      await this.startInstalledIntegrationAppsPolling();
    }
  }
}